import moment from 'moment';
import {
  AbstractComparatorApiService,
  AddressApiSuccessResponse,
  AddressData,
  CalculateParameters,
  CalculateParametersTripleplay,
  Comparison,
  CustormerBenefitItem,
  defaultCalculateParameters,
  houseNumberError,
  IComparatorApiServiceParameters,
  IProduct,
  OsTripleplay,
  OsTripleplayProduct,
} from '.';
import { LegacyOrderResponse } from '../../helpers';
import {
  HomeProduct,
  ProductCommission,
  ProductIncentiveDetail,
  ProductSupplier,
  ProductsWithSuppliers,
  ScoreEntity,
} from './graphProduct';
import {
  calculateOsTriplePlayQuery,
  calculateTriplePlayQuery,
  getHouseDataQuery,
  orderTriplePlay,
} from './graphql/queries';
import { addressDataQuery } from './graphql/queries/addressDataQuery';
import {
  calculateHomeQuery,
  calculationInputQuery,
  coverageBuildingsQuery,
  coverageHomesQuery,
  coverageLiabilitiesQuery,
  getHomeFeaturesQuery,
  orderHomeQuery,
  orderStatusQuery,
  productsWithSuppliersQuery,
} from './graphql/queries/home';
import { getCustomerBenefits } from './graphql/queries/triple-play/getCustomerBenefits';
import { getForms } from './graphql/queries/triple-play/getForms';
import { getProductsSupplier as getProductsSupplierQuery } from './graphql/queries/triple-play/getProductsSupplier';
import {
  getScoresEntities as getScoreEntitiesQuery,
  getScoreEntitiesVariables,
} from './graphql/queries/triple-play/getScoresEntities';

import { getComparison } from './graphql/queries/getComparison';
import {
  sendEmailToCustomerQuery,
  sendInactivityEmailToCustomerQuery,
} from './graphql/queries/home/sendEmailToCustomer';
import {
  CommonTechnical,
  FeaturesConnection,
  FeaturesEdge,
  MasterAgreementStructure,
  OrderResult,
  OrderStatusResult,
  Package,
} from './home';
import { Tripleplay, TripleplayProduct } from './index';
import {
  basicHomeIProduct,
  basicHomeProduct,
  basicHomeSupplier,
  DEFAULT_DISCOUNT,
  getHomeCoverages,
  getTypeFromQueryParams,
  getUspsBySellingPoints,
  QuoteResponse,
  TYPE_HOME,
  TYPE_OS_TRIPLEPLAY,
  TYPE_TRIPLEPLAY,
} from './product';
import { getProductType } from './triple-play/getProductType';
import { swiftMemberExistsQuery } from './graphql/queries/swiftMembership';

const defaultDropDownValues = {
  additionalCoverage: {
    0: '-1',
    1250000: '0',
    2500000: '1',
  },
  outsideCoverage: {
    2500: '1',
    5000: '2',
    7500: '3',
    10000: '4',
  },
  other: {
    1250: '1',
    2500: '2',
    5000: '3',
    7500: '4',
    10000: '5',
    15000: '6',
    20000: '7',
  },
  maxOwnRisk: {
    0: '1',
    150: '2',
    250: '3',
    500: '4',
  },
};

const generateUUID = (): string => {
  let d = new Date().getTime(); //Timestamp
  let d2 =
    (typeof performance !== 'undefined' &&
      performance.now &&
      performance.now() * 1000) ||
    0; //Time in microseconds since page-load or 0 if unsupported
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = self.crypto.getRandomValues(new Uint32Array(1))[0] % 17;
    if (d > 0) {
      //Use timestamp until depleted
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    } else {
      //Use microseconds since page-load if supported
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const handleResponse = (response: Response) => {
  return response.json().then((json) => {
    let data: any | undefined = json.data ? json.data : json;

    if (!response.ok) {
      const status = response.status;
      return Promise.reject(status);
    }

    return data;
  });
};

const buildGraphQL = (query: string, variables: string) => {
  let retVal: string;
  retVal = '{"query":"' + query.replace(/(\r\n|\n|\r)/g, '\\r\\n') + '"';
  retVal +=
    variables.length > 0
      ? ',"variables":' + variables.replace(/(\r\n|\n|\r)/g, '')
      : '';
  retVal += '}';
  return retVal;
};

const getCookie = (cookieName) => {
  let cookie = {};
  document.cookie.split(';').forEach((el) => {
    let [key, value] = el.split('=');
    cookie[key.trim()] = value;
  });
  return cookie[cookieName];
};

export const buildCharacteristics = (features) =>
  features
    ? features.map((feature) => {
        return {
          label: feature.name,
          value: feature.value,
        };
      })
    : [];

export function formatterTripleplay(
  products: TripleplayProduct[],
  type: IProduct['type'],
  comparisonId: string
): IProduct[] {
  return products.map((product) => {
    const {
      supplier,
      servicesWrapper,
      scoreEntity,
      contractInformation,
      packageCosts,
      customerBenefits,
      tags,
    } = product;
    const {
      about,
      advantages,
      disadvantages,
      externalIds,
      features,
      howItWorks,
      name,
      krn,
      nameLogoUrl,
      sellingPoints,
      taskDivisions,
      id,
      customAttributes,
    } = supplier;

    const cbDiscount = Number(packageCosts.cbDiscount) || 0;
    const discount = Number(packageCosts.discount) || cbDiscount || 0;

    const usps = product.specialOfferWrapper.specialOffer
      .filter((offer) => !!offer.text && offer.version !== 'algemeen')
      .map((offer) => ({
        text: offer.text,
        type: 'protext',
      }));

    const uspsWithCashback =
      discount > 10
        ? [
            {
              text: `€ ${discount.toFixed(2).replace('.', ',')} cashback`,
              type: 'cashback',
            },
            ...usps,
          ]
        : usps;

    const benefits = customerBenefits?.customerBenefit?.length
      ? customerBenefits.customerBenefit.map((el) => ({
          id: Number(el.product.id),
          description: el.advantages.advantages
            .map((advantage) => advantage.descriptions)
            .flatMap((descriptionItem) => descriptionItem.description)
            .filter((description) => description.type === 'samenvatting')
            .map((descriptionText) => descriptionText.remark),
        }))
      : [];

    const channels =
      servicesWrapper.televisionInfo?.channels?.channelsOverview?.television?.categories
        .flatMap((item) => item.channel)
        .map((el) => el.name) || [];

    const _servicesWrapper = {
      ...servicesWrapper,
      ...(servicesWrapper.televisionInfo !== null
        ? {
            televisionInfo: {
              ...servicesWrapper.televisionInfo,
              categories: [],
              channels: {
                ...servicesWrapper.televisionInfo?.channels,
                channelsOverview: [],
              },
            },
          }
        : {}),
    };

    const testScore =
      Number(
        scoreEntity?.scores
          .find((entity) => entity.name === 'Testoordeel')
          ?.rating.replace(',', '.')
      ) || 0;

    const packages =
      servicesWrapper.televisionInfo?.channels?.channelsPackages
        ?.channelPackages || null;

    const { onceOffCosts } = packageCosts;

    return {
      ...basicHomeIProduct,
      id: `${product.subscriptionId}`,
      subscriptionName: product.subscriptionName,
      orderFormId: product.orderFormId,
      priceInfo: {
        discountIncluded: discount,
        postage: Number(packageCosts.onceOffCosts.postageCosts),
        connection: Number(packageCosts.onceOffCosts.connectionCosts),
        priceList: packageCosts.monthlyCosts.priceList.priceList,
        initial: Number(packageCosts.total),
        initialDiscounted: Number(
          packageCosts.total - (packageCosts.discount ? 0 : DEFAULT_DISCOUNT)
        ),
        total: Number(packageCosts.monthlyCosts.standardRate),
        installation: packageCosts.onceOffCosts?.installations?.installation,
        providerDiscount: cbDiscount,
        discountSource: packageCosts?.discountSource,
      },
      image: nameLogoUrl,
      characteristics: buildCharacteristics(features),
      prosAndCons: {
        cons: {
          ...basicHomeIProduct.prosAndCons.cons,
          title1: disadvantages?.[0]?.description || '',
          title2: disadvantages?.[1]?.description || '',
        },
        pros: {
          ...basicHomeIProduct.prosAndCons.pros,
          title1: advantages?.[0]?.description || '',
          title2: advantages?.[1]?.description || '',
        },
      },
      provider: {
        ...basicHomeIProduct.provider,
        description: about,
        howItWorks,
        label: name,
        name,
        sellingPoints,
        taskDivisions,
        krn,
        id,
        externalId: externalIds[0].externalSupplierId,
      },
      purchasableProduct: true,
      testScore,
      packages,
      channels,
      customAttributes,
      usps: uspsWithCashback,
      type,
      servicesWrapper: _servicesWrapper,
      comparisonId,
      contractInformation: contractInformation || null,
      scoreEntity,
      benefits,
      onceOffCosts,
      tags,
    };
  });
}

const parsePrice = (price?: string): number =>
  parseFloat(price?.replace(',', '.') || '0');

const handleIncentives3P = ({
  productIncentiveDetails,
  productIncentives,
  incentiveAmount,
}) => {
  if (productIncentives === null || productIncentiveDetails.length === 0) {
    return null;
  }
  for (const incentiveItem of productIncentives) {
    return {
      ...productIncentiveDetails.find(
        (item) => item.krn === incentiveItem.productIncentiveDetailsKrn
      ),
      incentiveAmount,
    };
  }
};

export function formatterOsTripleplay(
  products,
  commonTechnical: CommonTechnical[],
  filters,
  productIncentiveDetails: ProductIncentiveDetail[]
) {
  return products
    .filter((product) => {
      const ziggoSport = product?.customProperties?.customAttributes?.find(
        (el) => el?.description === 'ziggoSport'
      )?.value
        ? 'true'
        : 'false';

      const espn = product?.customProperties?.customAttributes?.find(
        (el) => el?.description === 'ESPN'
      )?.value
        ? 'true'
        : 'false';

      if (
        (filters.includes('ziggoSport') && ziggoSport === 'false') ||
        (filters.includes('espn') && espn === 'false')
      ) {
        return false;
      }

      return product;
    })
    .map((product) => {
      const {
        affiliateUrl,
        supplier,
        refKey,
        totalInstallmentAmount,
        customProperties,
        productCode,
        tags,
        productIncentives,
      } = product;

      const {
        about,
        advantages,
        customAttributes,
        disadvantages,
        externalIds,
        features,
        howItWorks,
        name,
        krn,
        nameLogoUrl,
        sellingPoints,
        taskDivisions,
        id,
      } = supplier;

      const discount = 0;

      const durationForDiscount =
        Number(
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'durationForDiscount'
          )?.value
        ) || null;

      const discountInformation =
        customProperties?.customAttributes?.find(
          (el) => el?.description === 'discountInformation'
        )?.value || undefined;

      const priceMonthWithDiscount =
        customProperties?.customAttributes?.find(
          (el) => el?.description === 'priceMonthWithDiscount'
        )?.value || null;

      const onceOffCosts =
        customProperties?.customAttributes?.find(
          (el) => el?.description === 'onceOffCosts'
        )?.value || undefined;

      const discountThroughUs =
        customProperties?.customAttributes?.find(
          (el) => el?.description === 'discountThroughUs'
        )?.value || undefined;

      const discountProvider =
        customProperties?.customAttributes?.find(
          (el) => el?.description === 'discountProvider'
        )?.value || undefined;

      const ziggoSport = customProperties?.customAttributes?.find(
        (el) => el?.description === 'ziggoSport'
      )?.value
        ? 'true'
        : 'false';

      const espn = customProperties?.customAttributes?.find(
        (el) => el?.description === 'ESPN'
      )?.value
        ? 'true'
        : 'false';

      const ziggoSportPrice =
        customProperties?.customAttributes?.find(
          (el) => el?.description === 'ziggoSportPrice'
        )?.value || undefined;

      const espnPrice =
        customProperties?.customAttributes?.find(
          (el) => el?.description === 'ESPNPrice'
        )?.value || undefined;

      const recommendation = product?.customProperties?.customAttributes?.find(
        (el) => el?.description === 'suggestedTriplePlay'
      )?.value || undefined;

      const showZiggoSport =
        filters.includes('ziggoSport') && ziggoSport === 'true';
      const showEspn = filters.includes('espn') && espn === 'true';

      const totalPerMonth =
        totalInstallmentAmount +
        (showZiggoSport ? parsePrice(ziggoSportPrice) : 0) +
        (showEspn ? parsePrice(espnPrice) : 0);
      const totalYearlyCost = totalPerMonth * 12;
      const discountFirstMonths = durationForDiscount
        ? (totalInstallmentAmount - parsePrice(priceMonthWithDiscount)) *
          Number(durationForDiscount)
        : 0;

      const totalFirstYearCost =
        totalYearlyCost -
        discountFirstMonths +
        parsePrice(onceOffCosts) -
        parsePrice(discountThroughUs) -
        parsePrice(discountProvider);

      // const uspsWithCashback = discount > 10 && [
      //   {
      //     text: `€ ${discount.toFixed(2).replace('.', ',')} cashback`,
      //     type: 'cashback',
      //   },
      // ];

      const benefits = product?.customerBenefits?.customerBenefit?.length
        ? product?.customerBenefits.customerBenefit.map((el) => ({
            id: Number(el.product.id),
            description: el.advantages.advantages
              .map((advantage) => advantage.descriptions)
              .flatMap((descriptionItem) => descriptionItem.description)
              .filter((description) => description.type === 'samenvatting')
              .map((descriptionText) => descriptionText.remark),
          }))
        : [];

      const testScore =
        Number(
          product?.scoreEntity?.scores
            .find((entity) => entity.name === 'Testoordeel')
            ?.rating.replace(',', '.')
        ) || 0;

      const packages =
        product?.servicesWrapper?.televisionInfo?.channels?.channelsPackages
          ?.channelPackages || null;

      return {
        ...basicHomeIProduct,
        id: refKey,
        affiliateUrl,
        commonTechnical,
        customAttributes,
        subscriptionName:
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'productName'
          )?.value ||
          product?.name ||
          '',
        productCode,
        priceInfo: {
          totalFirstYearCost,
          discountIncluded: discount,
          postage: totalInstallmentAmount,
          connection: totalInstallmentAmount,
          priceList: 0,
          initial: totalInstallmentAmount,
          initialDiscounted: totalInstallmentAmount - discount,
          total: totalInstallmentAmount,
          installation: totalInstallmentAmount,
          providerDiscount: discount,
          discountSource: 'discountThroughUs',
        },
        image: nameLogoUrl,
        characteristics: buildCharacteristics(features),
        prosAndCons: {
          cons: {
            ...basicHomeIProduct.prosAndCons.cons,
            title1: disadvantages?.[0]?.description || '',
            title2: disadvantages?.[1]?.description || '',
          },
          pros: {
            ...basicHomeIProduct.prosAndCons.pros,
            title1: advantages?.[0]?.description || '',
            title2: advantages?.[1]?.description || '',
          },
        },
        provider: {
          ...basicHomeIProduct.provider,
          description: about,
          howItWorks,
          label: name,
          name,
          sellingPoints,
          taskDivisions,
          krn,
          id,
          externalId: externalIds[0].externalSupplierId,
        },
        incentive: handleIncentives3P({
          productIncentiveDetails,
          productIncentives,
          incentiveAmount: discountProvider || 0,
        }),
        purchasableProduct: true,
        testScore,
        packages,
        usps: getUspsBySellingPoints(product?.sellingPoints || sellingPoints),
        type: 'os-triple-play',
        comparisonId: commonTechnical[0].transactionReference,
        contractInformation: product?.contractInformation || null,
        benefits,
        tags:
          tags !== null
            ? tags[0]
            : customAttributes?.[0]?.description === '3Play Top 3'
              ? 'featured'
              : null,
        category:
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'category'
          )?.value || undefined,
        durationForDiscount,
        priceMonthWithDiscount,
        discountInformation,
        downloadSpeedInMb:
          Number(
            customProperties?.customAttributes?.find(
              (el) => el?.description === 'downloadSpeedInMb'
            )?.value
          ) || undefined,
        uploadSpeedInMb:
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'uploadSpeedInMb'
          )?.value || undefined,
        averageDeliveryTimeInDays:
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'averageDeliveryTimeInDays'
          )?.value || undefined,
        network:
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'network'
          )?.value || undefined,
        tvChannels:
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'tvChannels'
          )?.value || undefined,
        hdChannels:
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'hdChannels'
          )?.value || undefined,
        onceOffCosts,
        discountThroughUs,
        discountProvider,
        ziggoSport,
        ziggoSportPrice,
        espn,
        espnPrice,
        recommendation,
        combinationDiscount:
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'combinationDiscount'
          )?.value || undefined,
        combinationDiscountInformation:
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'combinationDiscountInformation'
          )?.value || undefined,
        durationInMonths:
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'durationInMonths'
          )?.value || undefined,
        reviewAndPause:
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'reviewAndPause'
          )?.value === 'Ja',
        numberPorting:
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'numberPorting'
          )?.value === 'Ja',
        unlimitedCallingInNetherlands: customProperties?.customAttributes?.find(
          (el) => el?.description === 'unlimitedCallingInNetherlands'
        )?.value
          ? 'true'
          : 'false',
        unlimitedCallingInEurope: customProperties?.customAttributes?.find(
          (el) => el?.description === 'unlimitedCallingInEurope'
        )?.value
          ? 'true'
          : 'false',
        switchingService:
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'switchingService'
          )?.value === 'Ja',
        installationOptions:
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'installationOptions'
          )?.value === 'Ja',
        tvViaApp:
          customProperties?.customAttributes?.find(
            (el) => el?.description === 'tvViaApp'
          )?.value === 'Ja',
      };
    });
}

function removeDuplicateDocuments(obj) {
  const allObjects = Object.values(obj).flat();

  const uniqueLabels = new Map();

  const uniqueObjects = allObjects.filter((item: any) => {
    if (!uniqueLabels.has(item.label)) {
      uniqueLabels.set(item.label, true);
      return true;
    }
    return false;
  });

  const result = {};
  Object.keys(obj).forEach((key) => {
    result[key] = uniqueObjects.filter((item) => obj[key].includes(item));
  });

  return result;
}

const getRiskCoverageConditions = (policyPolicyDetails) => {
  const coverageTypes = [
    'coverageBuildings',
    'coverageHomes',
    'coverageLiabilities',
  ];
  const extractedData = {};

  coverageTypes.forEach((coverageType) => {
    policyPolicyDetails?.map((policyDetails) => {
      if (policyDetails[coverageType] !== null) {
        const coverageData = policyDetails[coverageType]
          .map((coverage) => {
            return coverage.conditionConditionDetails.map((condition) => {
              return {
                label: condition.conditionText,
                urlAttachment: condition.documents[0].urlAttachment,
              };
            });
          })
          .flat();

        extractedData[coverageType] =
          coverageData.length > 0 ? coverageData : null;
      }
    });
  });
  return removeDuplicateDocuments(extractedData);
};

const getRiskCoverageDocuments = (policyPolicyDetails) => {
  const coverageTypes = [
    'coverageBuildings',
    'coverageHomes',
    'coverageLiabilities',
  ];
  let extractedDocuments: {
    url: string;
    label: string;
  }[] = [];
  let documentsFound = false;

  for (const coverageType of coverageTypes) {
    if (policyPolicyDetails[coverageType] !== null && !documentsFound) {
      const documents = policyPolicyDetails[coverageType][0].documents;

      if (documents && documents.length > 0) {
        extractedDocuments = documents.map((d) => ({
          url: d.urlAttachment,
          label: d.attachmentDescription,
        }));
        documentsFound = true;
        break;
      }
    }
  }

  return documentsFound ? extractedDocuments : undefined;
};

const handleIncentives = ({ productIncentiveDetails, productIncentives }) => {
  if (productIncentives === null || productIncentiveDetails.length === 0) {
    return null;
  }
  for (const incentiveItem of productIncentives) {
    return productIncentiveDetails.find(
      (item) => item.krn === incentiveItem.productIncentiveDetailsKrn
    );
  }
};

export function formatterHome(
  packages: Package[],
  products: HomeProduct[],
  suppliers: ProductSupplier[],
  featuresEdges: FeaturesEdge[],
  commonTechnical: CommonTechnical[],
  type: IProduct['type'],
  productIncentiveDetails: ProductIncentiveDetail[],
  productCommissions: ProductCommission[]
): IProduct[] {
  return packages
    .filter((item) => item.totalInstallmentAmount !== null)
    .filter((item) => {
      const product = products.find((p) => p.krn === item.productCode);
      return !(
        item.underwritingCompanySpecifics[0]?.underwritingResult === 'V2' &&
        product?.orderType === 'FUNNEL_MGA'
      );
    })
    .map((homePackage) => {
      const {
        customProperties,
        offerNumber,
        packageDiscountInstallmentAmount,
        productCode,
        productDescription,
        totalInstallmentAmount,
        orderFormId,
        externalSourceName,
      } = homePackage;

      const productCodes = homePackage.policyPolicyDetails.map(
        (policy) => policy.productCode
      );

      const urls = productCodes.map((productCode) => {
        const p = products.find((product) => product.krn === productCode);
        if (p?.productType) {
          return {
            coverageType: p?.productType,
            insuranceCardUrl: p?.insuranceCardUrl,
            termsAndConditionsUrl: p?.termsAndConditionsUrl,
          };
        }
        return null;
      });

      const product: HomeProduct = {
        ...basicHomeProduct,
        ...products.find((p) => p.krn === productCode),
      };
      const supplier: ProductSupplier = {
        ...basicHomeSupplier,
        ...suppliers.find((s) => s.krn === customProperties.supplierKrn),
      };

      let productCommission;

      if (productCodes.length === 1) {
        const matchedCommission = productCommissions.find((p) =>
          productCodes.includes(p.krn)
        );
        productCommission = {
          krn: '',
          renewalCommissionInstallmentAmountPartner: null,
          commissionConfigs: [
            {
              renewalCommissionInstallmentNumberOfMonths: null,
              renewalCommissionInstallmentAmountLink: null,
            },
          ],
          ...matchedCommission,
        };
      } else {
        const matchedCommissions = productCommissions.filter((p) =>
          productCodes.includes(p.krn)
        );
        const totalRenewalCommission = matchedCommissions.reduce((sum, p) => {
          return sum + (p.renewalCommissionInstallmentAmountPartner || 0);
        }, 0);

        const validCommission = matchedCommissions.find(
          (p) =>
            p.commissionConfigs[0]
              .renewalCommissionInstallmentNumberOfMonths !== null &&
            p.commissionConfigs[0].renewalCommissionInstallmentAmountLink !==
              null
        );
        const commissionConfigs = validCommission
          ? validCommission.commissionConfigs
          : [
              {
                renewalCommissionInstallmentNumberOfMonths: null,
                renewalCommissionInstallmentAmountLink: null,
              },
            ];

        productCommission = {
          krn: '',
          renewalCommissionInstallmentAmountPartner: totalRenewalCommission
            .toFixed(2)
            .replace('.', ','),
          commissionConfigs: commissionConfigs,
        };
      }

      // console.log(productCommissions.find((p) => productCodes.includes(p.krn)));
      const productId = parseInt(product.externalId);
      const coverages = getHomeCoverages(homePackage);

      const coverageValue =
        coverages.coverageHomes?.deductibleAmount ||
        coverages.coverageBuildings?.deductibleAmount;

      const ownFeatures = featuresEdges.filter((feature) => {
        return [feature.node.parentProductId, feature.node.productId]?.includes(
          productId
        );
      });
      const {
        affiliateUrl,
        customAttributes,
        insuranceCardUrl,
        name: productName,
        orderType,
        productIncentives,
        productType,
        sellingPoints: productSellingPoints,
        termsAndConditionsUrl,
        testScores,
      } = product;

      const {
        about,
        advantages,
        disadvantages,
        features,
        howItWorks,
        name,
        nameLogoUrl,
        sellingPoints,
        sustainabilityScore,
        taskDivisions,
      } = supplier;

      let visibleAffiliateUrl = '';

      if (orderType === 'AFFILIATE') {
        visibleAffiliateUrl = affiliateUrl;
      }
      return {
        ...basicHomeIProduct,
        affiliateUrl: visibleAffiliateUrl,
        commonTechnical: commonTechnical,
        bigProductFeature: {
          ...basicHomeIProduct.bigProductFeature,
          decimals: 0,
          label: 'Eigen risico',
          hideIfZero: false,
          variant: 'priceMedium',
          value: {
            month: coverageValue || 0,
            year: coverageValue || 0,
          },
        },
        characteristics: buildCharacteristics(features),
        customAttributes,
        supplierCustomAttributes: supplier.customAttributes,
        fees: ownFeatures.map(({ node }) => {
          return {
            category: node.category,
            label: node.name,
            value: node.data,
          };
        }),
        homePackage,
        id: `${productCode}-${offerNumber}`,
        image: nameLogoUrl,
        orderType,
        priceInfo: {
          ...basicHomeIProduct.priceInfo,
          initial: totalInstallmentAmount + packageDiscountInstallmentAmount,
          total: totalInstallmentAmount,
        },
        productCode,
        productType,
        prosAndCons: {
          cons: {
            ...basicHomeIProduct.prosAndCons.cons,
            title1: disadvantages?.[0]?.description || '',
            title2: disadvantages?.[1]?.description || '',
          },
          pros: {
            ...basicHomeIProduct.prosAndCons.pros,
            title1: advantages?.[0]?.description || '',
            title2: advantages?.[1]?.description || '',
          },
        },
        provider: {
          ...basicHomeIProduct.provider,
          description: about,
          howItWorks,
          insuranceCardUrl,
          label: name + ' ' + productDescription,
          name,
          sellingPoints,
          sustainabilityScore: sustainabilityScore
            ? `${sustainabilityScore[0]?.score}`
            : '',
          taskDivisions,
          termsAndConditionsUrl,
          urls: {
            household: urls.find(
              (e) => e?.coverageType === 'insurance/damage/household'
            ),
            building: urls.find(
              (e) => e?.coverageType === 'insurance/damage/building'
            ),
            liability: urls.find(
              (e) => e?.coverageType === 'insurance/damage/liability'
            ),
          },
          ...(externalSourceName === 'krn:kizi:data-provider:risk'
            ? {
                coverageConditions: getRiskCoverageConditions(
                  homePackage?.policyPolicyDetails ?? []
                ),
                documents: getRiskCoverageDocuments(
                  homePackage?.policyPolicyDetails[0] ?? []
                ),
              }
            : {}),
          id: supplier.krn,
          type:
            externalSourceName === 'krn:kizi:data-provider:risk'
              ? 'risk'
              : 'moneyview',
        },
        purchasableProduct:
          orderType !== 'NOT_ORDERABLE'
            ? ['V2', 'V3'].includes(
                homePackage?.underwritingCompanySpecifics[0]?.underwritingResult
              )
              ? false
              : true
            : false,
        testScore:
          homePackage.policyPolicyDetails.length === 1
            ? products.find(
                (product) =>
                  product.krn === homePackage.policyPolicyDetails[0].productCode
              )?.testScores?.[0]?.score || 0
            : testScores?.[0]?.score || 0,
        usps: getUspsBySellingPoints(productSellingPoints || sellingPoints),
        type,
        orderFormId,
        coverages,
        incentive: handleIncentives({
          productIncentiveDetails,
          productIncentives,
        }),
        subscriptionName:
          homePackage.policyPolicyDetails.length === 1
            ? products.find(
                (product) =>
                  product.krn === homePackage.policyPolicyDetails[0].productCode
              )?.name
            : productName,
        displayedPrice: totalInstallmentAmount,
        commissionTransparency: {
          renewalCommissionInstallmentAmountPartner:
            productCommission?.renewalCommissionInstallmentAmountPartner,
          renewalCommissionInstallmentNumberOfMonths:
            productCommission?.commissionConfigs?.[0]
              ?.renewalCommissionInstallmentNumberOfMonths,
          renewalCommissionInstallmentAmountLink:
            productCommission?.commissionConfigs?.[0]
              ?.renewalCommissionInstallmentAmountLink,
        },
      };
    });
}

const getSafeData = (data: any, def: any): any => {
  return data || def;
};

export const getCalculateSessionId = (): string => {
  let calculateSessionID = localStorage.getItem('calculateSessionId') ?? '';

  if (!calculateSessionID) {
    calculateSessionID = generateUUID();
    localStorage.setItem('calculateSessionId', calculateSessionID);
  }

  return calculateSessionID;
};

const getDeductibleAmount = (value: string): number | null => {
  let retVal: number | null = 0;

  switch (value) {
    case '0':
      retVal = null;
      break;
    case '2':
      retVal = 150;
      break;
    case '3':
      retVal = 250;
      break;
    case '4':
      retVal = 500;
      break;
    default:
      break;
  }

  return retVal;
};

const getAdditionalCoverage = (value: string): number => {
  let retVal = 0;

  switch (value) {
    case '0':
      retVal = 1250000.0;
      break;
    case '1':
      retVal = 2500000.0;
      break;
  }

  return retVal;
};

const getDefaultCoverage = (value: string): number => {
  let retVal = 0;

  switch (value) {
    case '1':
      retVal = 1250.0;
      break;
    case '2':
      retVal = 2500.0;
      break;
    case '3':
      retVal = 5000.0;
      break;
    case '4':
      retVal = 7500.0;
      break;
    case '5':
      retVal = 10000.0;
      break;
    case '6':
      retVal = 15000.0;
      break;
    case '7':
      retVal = 20000.0;
      break;
  }

  return retVal;
};

const getOutsideCoverageAmount = (value: string): number => {
  let retVal = 0;

  switch (value) {
    case '1':
      retVal = 2500;
      break;
    case '2':
      retVal = 5000;
      break;
    case '3':
      retVal = 7500;
      break;
    case '4':
      retVal = 10000;
      break;
  }

  return retVal;
};

const getTenantAmountInterest = (value: string): number => {
  let retVal = 0;

  switch (value) {
    case '1':
      retVal = 1000;
      break;
    case '2':
      retVal = 6000;
      break;
    case '3':
      retVal = 10000;
      break;
    case '4':
      retVal = 15000;
      break;
  }

  return retVal;
};

const isArrayNotEmpty = (array: object): boolean => {
  return Array.isArray(array) && array.length > 0;
};

const getHomeCalculateParameters = (
  calculateParameters: CalculateParameters,
  selectedFilters: object
) => {
  calculateParameters.deductibleAmount = getDeductibleAmount(
    isArrayNotEmpty(selectedFilters['maxOwnRisk'])
      ? selectedFilters['maxOwnRisk'][0]
      : '0'
  );

  calculateParameters.includeCoverageLiabilities = isArrayNotEmpty(
    selectedFilters['additionalCoverage']
  );

  calculateParameters.audioTV = getDefaultCoverage(
    isArrayNotEmpty(selectedFilters['audio_tv'])
      ? selectedFilters['audio_tv'][0]
      : '0'
  );

  calculateParameters.computers = getDefaultCoverage(
    isArrayNotEmpty(selectedFilters['computers'])
      ? selectedFilters['computers'][0]
      : '0'
  );

  calculateParameters.antique = getDefaultCoverage(
    isArrayNotEmpty(selectedFilters['antique'])
      ? selectedFilters['antique'][0]
      : '0'
  );

  calculateParameters.collection = getDefaultCoverage(
    isArrayNotEmpty(selectedFilters['collection'])
      ? selectedFilters['collection'][0]
      : '0'
  );

  calculateParameters.jewelry = getDefaultCoverage(
    isArrayNotEmpty(selectedFilters['jewelry'])
      ? selectedFilters['jewelry'][0]
      : '0'
  );

  calculateParameters.art = getDefaultCoverage(
    isArrayNotEmpty(selectedFilters['art']) ? selectedFilters['art'][0] : '0'
  );

  calculateParameters.instruments = getDefaultCoverage(
    isArrayNotEmpty(selectedFilters['instruments'])
      ? selectedFilters['instruments'][0]
      : '0'
  );

  calculateParameters.outsideCoverage = getOutsideCoverageAmount(
    isArrayNotEmpty(selectedFilters['outsideCoverage'])
      ? selectedFilters['outsideCoverage'][0]
      : '0'
  );

  return calculateParameters;
};

const replaceCoverageHomeQuery = (
  calculateParameters: CalculateParameters,
  query: string
): string => {
  return calculateParameters.includeCoverageHomes
    ? query.replace(
        '"$coverageHomes"',
        coverageHomesQuery
          .replace(
            '$coverageCodeHome',
            calculateParameters.coverageCodeHome ?? ''
          )
          .replace(
            '$outsideCoverage',
            (calculateParameters.outsideCoverage ?? 0).toString(10)
          )
          .replace(
            '$amountAudioVideo',
            (calculateParameters.audioTV ?? 0).toString(10)
          )
          .replace(
            '$computerEquipment',
            (calculateParameters.computers ?? 0).toString(10)
          )
          .replace(
            '$collection',
            (calculateParameters.collection ?? 0).toString(10)
          )
          .replace('$jewelry', (calculateParameters.jewelry ?? 0).toString(10))
          .replace('$antique', (calculateParameters.antique ?? 0).toString(10))
          .replace('$art', (calculateParameters.art ?? 0).toString(10))
          .replace(
            '$instruments',
            (calculateParameters.instruments ?? 0).toString(10)
          )
          .replace(
            '$homeSumInsured',
            (calculateParameters.homeSumInsured ?? 0).toString(10)
          )
          .replace(
            '$deductibleAmount',
            `${calculateParameters.deductibleAmount}`
            // (calculateParameters.deductibleAmount ?? 0).toString(10)
          )
          .replace(
            '$insuredFamilyComposition',
            (calculateParameters.insuredFamilyComposition ?? 0).toString(10)
          )
          .replace(
            '$tenantInterest',
            (calculateParameters.tenantInterest ?? 0).toString(10)
          )
      )
    : query.replace('"$coverageHomes"', '[]');
};

const getGlass = (
  aditionalCoverages: Array<string>,
  selectedFilters: object
): string => {
  let retVal = 'V15';

  const glassSelected =
    Array.isArray(selectedFilters['glass']) &&
    selectedFilters['glass'].length > 0 &&
    selectedFilters['glass'][0] !== '-1';

  if (aditionalCoverages.includes('glass') || glassSelected) {
    retVal = 'V16';
  }

  return retVal;
};

const shouldIncludeAllRisk = (
  aditionalCoverages: Array<string>,
  selectedFilters: object
): boolean => {
  const allRiskSelected =
    Array.isArray(selectedFilters['all_risk']) &&
    selectedFilters['all_risk'].length > 0 &&
    selectedFilters['all_risk'][0] !== '-1';

  return aditionalCoverages.includes('allrisk') || allRiskSelected;
};

const getCoverageCode = (coverageHomes, coverageBuildings) => {
  if (coverageHomes.length > 0) {
    if (coverageBuildings.length > 0) {
      return '3';
    }
    return '1';
  }
  return '2';
};

const getFamilyComposition = (coverageHomes, coverageBuildings) => {
  if (coverageHomes.length === 0) {
    return coverageBuildings[0].insuredFamilyComposition;
  }
  return coverageHomes[0].insuredFamilyComposition;
};

const getSumInsured = (coverageHomes, coverageBuildings) => {
  if (coverageHomes.length === 0) {
    return coverageBuildings[0].sumInsured;
  }
  return coverageHomes[0].sumInsured;
};

const calculateCoverageAdditions = (
  coverageHomes,
  coverageBuildings,
  coverageLiabilities,
  objectGlasses
) => {
  let coverageadditions: string[] = [];

  if (
    objectGlasses.length > 0 &&
    objectGlasses[0].translucentMaterialType === 'V16'
  ) {
    coverageadditions.push('glass');
  }

  if (
    coverageLiabilities.length > 0 &&
    coverageLiabilities[0].sumInsured === 1250000
  ) {
    coverageadditions.push('legal');
  }

  if (coverageHomes.length === 0) {
    if (coverageBuildings[0].coverageCode === 'V5006') {
      coverageadditions.push('allrisk');
    }
    return coverageadditions;
  }

  if (coverageHomes.length > 0 && coverageHomes[0].coverageCode === 'V5016') {
    coverageadditions.push('allrisk');
  }

  if (
    coverageHomes.length > 0 &&
    coverageHomes[0].insuredAmountValuablesOutdoors === 2500
  ) {
    coverageadditions.push('outsidecoverage');
  }

  return coverageadditions;
};

const calculateFilters = (packages, coveragetype) => {
  return {
    showPriceMode: [packages.paymentTermInMonths === 1 ? 'month' : 'year'],
    providerName: [],
    filteropties: [],
    sustainabilityScore: [],
    others: [],
    maxOwnRisk:
      packages.coverageLiabilities.length > 0
        ? [
            calculateHomeFilterValues(
              packages.coverageLiabilities[0].deductibleAmount,
              'maxOwnRisk'
            ),
          ]
        : [],
    additionalCoverage:
      packages.coverageLiabilities.length > 0
        ? [
            calculateHomeFilterValues(
              packages.coverageLiabilities[0].sumInsured,
              'additionalCoverage'
            ),
          ]
        : [],
    audio_tv: [
      coveragetype === '2'
        ? '-1'
        : calculateHomeFilterValues(
            packages.coverageHomes[0].customProperties.insuredAmountAudioVideo
          ),
    ],
    computers: [
      coveragetype === '2'
        ? '-1'
        : calculateHomeFilterValues(
            packages.coverageHomes[0].insuredAmountComputerEquipment
          ),
    ],
    collection: [
      coveragetype === '2'
        ? '-1'
        : calculateHomeFilterValues(
            packages.coverageHomes[0].insuredAmountCollections
          ),
    ],
    jewelry: [
      coveragetype === '2'
        ? '-1'
        : calculateHomeFilterValues(
            packages.coverageHomes[0].insuredAmountJewelry
          ),
    ],
    antique: [
      coveragetype === '2'
        ? '-1'
        : calculateHomeFilterValues(
            packages.coverageHomes[0].customProperties.insuredAmountAntiques
          ),
    ],
    art: [
      coveragetype === '2'
        ? '-1'
        : calculateHomeFilterValues(
            packages.coverageHomes[0].customProperties.insuredAmountArt
          ),
    ],
    outsideCoverage: [
      coveragetype === '2'
        ? '-1'
        : calculateHomeFilterValues(
            packages.coverageHomes[0].insuredAmountValuablesOutdoors,
            'outsideCoverage'
          ),
    ],
    instruments: [
      coveragetype === '2'
        ? '-1'
        : calculateHomeFilterValues(
            packages.coverageHomes[0].insuredAmountMusicalInstruments
          ),
    ],
  };
};

const calculateHomeFilterValues = (
  coverageAmount: number,
  dropdownName = 'other'
) => {
  if (defaultDropDownValues[dropdownName][coverageAmount]) {
    return defaultDropDownValues[dropdownName][coverageAmount];
  }
  return '-1';
};

const getAnalyticsSessionIdCookieName = () => {
  const cookieNameStart = document.cookie.substring(
    document.cookie.indexOf('_ga_')
  );
  return cookieNameStart.substring(0, cookieNameStart.indexOf('='));
};

export class GraphLegacyApiService extends AbstractComparatorApiService {
  private calculateSessionID = '';

  tokenUrl = '';

  baseUrl = '';

  private client: any;

  constructor(params: IComparatorApiServiceParameters) {
    super(params);

    for (const attr in params) {
      this[attr] = params[attr];
    }
  }

  setClient(client: any) {
    this.client = client;
  }

  getClient(): any {
    return this.client;
  }

  getQuotePrefill(
    accessToken: string,
    swiftUserId: string,
    swiftProductId: string,
    productType: string
  ): Promise<false | QuoteResponse> | undefined {
    console.log(swiftUserId, swiftProductId, productType, accessToken);
    return undefined;
  }

  async getHeaders(headers: object): Promise<any> {
    if (
      this.credentials.token === '' ||
      moment().isAfter(this.credentials.expiresIn)
    ) {
      const response = await this.acquireToken();
      const payload = response.ok && response.json ? await response.json() : '';
      this.credentials.token = payload.access_token;
      this.credentials.expiresIn = moment().add(
        payload.expires_in - 1,
        'seconds'
      );
    }

    return {
      headers: {
        ...headers,
        authorization: `Bearer ${this.credentials.token}`,
      },
    };
  }

  async acquireToken(): Promise<Response> {
    const scope = process.env.KIZI_PUBLIC_API_SCOPE;

    let body =
      'client_id=' +
      this.credentials.clientId +
      '&client_secret=' +
      this.credentials.clientSecret +
      '&grant_type=client_credentials';

    if (scope) {
      body += '&scope=' + scope;
    }

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    };

    if (this.tokenUrl && this.tokenUrl.length > 0) {
      return fetch(this.tokenUrl, requestOptions);
    } else {
      return Promise.reject('Invalid Token url');
    }
  }

  async getProductsHome(
    _searchCriteria: object,
    selectedFilters: object
  ): Promise<IProduct[]> {
    console.log({_searchCriteria});
    let calculateParameters: CalculateParameters = {
      ...defaultCalculateParameters,
      postalCode: getSafeData(_searchCriteria['postcode'], ''),
      effectiveDate: new Date().toISOString().slice(0, 10),
      houseNumber: getSafeData(_searchCriteria['housenumber'], ''),
    };

    const paymentTermInMonths = getSafeData(
      Array.isArray(selectedFilters['showPriceMode']) &&
        selectedFilters['showPriceMode'].length > 0
        ? selectedFilters['showPriceMode'][0]
        : 'month',
      'month'
    );

    calculateParameters = getHomeCalculateParameters(
      calculateParameters,
      selectedFilters
    );

    calculateParameters.houseLetter = getSafeData(
      _searchCriteria['housenumberaddition'],
      calculateParameters.houseLetter
    );
    calculateParameters.insuredFamilyComposition = getSafeData(
      _searchCriteria['familyComposition'],
      calculateParameters.insuredFamilyComposition
    );

    calculateParameters.paymentTermInMonths =
      paymentTermInMonths == 'month' ? 1 : 12;

    calculateParameters.homeSumInsured = getSafeData(
      _searchCriteria['homeSumInsured'],
      calculateParameters.homeSumInsured
    );
    calculateParameters.constructionYear = getSafeData(
      Number(_searchCriteria['constructionyear']),
      calculateParameters.constructionYear
    );
    calculateParameters.constructionOfRoof = getSafeData(
      //_searchCriteria['constructionOfRoof'],
      _searchCriteria['rooftype'],
      calculateParameters.constructionOfRoof
    );
    calculateParameters.homeType = getSafeData(
      _searchCriteria['addressHomeType'],
      calculateParameters.homeType
    );
    calculateParameters.propertyUseType = getSafeData(
      _searchCriteria['propertyUseType'],
      calculateParameters.propertyUseType
    );
    calculateParameters.rebuildCost = getSafeData(
      _searchCriteria['rebuildCost'],
      calculateParameters.rebuildCost
    );
    calculateParameters.consideredMonumentByOwner = getSafeData(
      _searchCriteria['consideredMonumentByOwner'],
      calculateParameters.consideredMonumentByOwner
    );
    calculateParameters.houseSurfaceAreaSquareMeters = getSafeData(
      Number(_searchCriteria['surfacesize']),
      calculateParameters.houseSurfaceAreaSquareMeters
    );
    calculateParameters.numberRooms = getSafeData(
      Number(_searchCriteria['numberrooms']),
      calculateParameters.numberRooms
    );
    calculateParameters.constructionOfWalls =
      getSafeData(
        _searchCriteria['consturctionwalls'],
        calculateParameters.constructionOfWalls
      ) === 1
        ? 'VT'
        : 'VK';
    calculateParameters.constructionOfFloors =
      getSafeData(
        _searchCriteria['mezzaninefloor'],
        calculateParameters.constructionOfFloors
      ) === 1
        ? 'VS'
        : 'VH';
    calculateParameters.propertyOfInsured = getSafeData(
      _searchCriteria['userhometype'] === '2',
      calculateParameters.propertyOfInsured
    );
    calculateParameters.volumeInCubicMeters = getSafeData(
      Number(_searchCriteria['contentsize']),
      calculateParameters.volumeInCubicMeters
    );
    calculateParameters.wozAppraisalValue = getSafeData(
      _searchCriteria['wozAppraisalValue'],
      calculateParameters.wozAppraisalValue
    );
    calculateParameters.netMonthlyIncome = Number.parseFloat(
      getSafeData(
        _searchCriteria['income'],
        calculateParameters.netMonthlyIncome
      )
    );
    calculateParameters.burglaryPrevention = getSafeData(
      _searchCriteria['security'],
      calculateParameters.burglaryPrevention
    );

    calculateParameters.solarPanels = getSafeData(
      _searchCriteria['solarPanels'],
      calculateParameters.solarPanels
    );
    calculateParameters.recentBathroomKitchen = getSafeData(
      _searchCriteria['renovated'],
      calculateParameters.recentBathroomKitchen
    );
    const safeDate = getSafeData(
      _searchCriteria['birthdate'],
      calculateParameters.birthDate
    );

    calculateParameters.birthDate = moment(safeDate, 'DD-MM-YYYY').format(
      'YYYY-MM-DD'
    );
    calculateParameters.tenantInterest = getTenantAmountInterest(
      _searchCriteria['tenantInterestAmount'] || '0'
    );

    const aditionalCoverages = getSafeData(
      _searchCriteria['coverageadditions'],
      []
    );

    if (
      !calculateParameters.includeCoverageLiabilities &&
      aditionalCoverages.includes('legal')
    ) {
      calculateParameters.includeCoverageLiabilities = true;
    }

    calculateParameters.glass = getGlass(aditionalCoverages, selectedFilters);

    if (shouldIncludeAllRisk(aditionalCoverages, selectedFilters)) {
      calculateParameters.coverageCodeBuildings = 'V5006';
      calculateParameters.coverageCodeHome = 'V5016';
    }

    if (aditionalCoverages.includes('outsidecoverage')) {
      if (calculateParameters.outsideCoverage == 0) {
        calculateParameters.outsideCoverage = 2500;
      }
    }

    calculateParameters.liabilitySumInsured = getAdditionalCoverage(
      calculateParameters.includeCoverageLiabilities
        ? selectedFilters['additionalCoverage'][0]
        : '-1'
    );

    const selectedCoverage = getSafeData(_searchCriteria['coveragetype'], 3);

    switch (parseInt(selectedCoverage)) {
      case 1:
        calculateParameters.includeCoverageHomes = true;
        calculateParameters.includeCoverageBuildings = false;
        break;
      case 2:
        calculateParameters.includeCoverageHomes = false;
        calculateParameters.includeCoverageBuildings = true;
        break;
      default:
        calculateParameters.includeCoverageHomes = true;
        calculateParameters.includeCoverageBuildings = true;
        break;
    }

    let features: FeaturesEdge[] = [];
    let packages: Package[] = [];
    let products: HomeProduct[] = [];
    let productCommissions: ProductCommission[] = [];
    let suppliers: ProductSupplier[] = [];
    let commonTechnical: CommonTechnical[] = [];
    let productIncentiveDetails: ProductIncentiveDetail[] = [];

    await this.calculateHomeContentPackage(calculateParameters)
      .then(async (data) => {
        packages = data.masterAgreementPackages;
        const packageKrns: string[] = packages.map((item) => {
          return item.productCode;
        });
        let productKrns: string[] = [];
        packages.map((masterPackage) => {
          masterPackage.policyPolicyDetails.map((policy) => {
            if (policy.productCode) {
              productKrns.push(policy.productCode);
            }
          });
        });

        if (Array.isArray(data.commonTechnical)) {
          commonTechnical = data.commonTechnical;
        }

        const payloadKrns = [...packageKrns, ...productKrns];
        await this.getProductsWithSupliers(payloadKrns, 'HOME')
          .then(async (response) => {
            products = response.products;
            suppliers = response.suppliers;
            productIncentiveDetails = response.productIncentiveDetails ?? [];
            productCommissions = response?.productCommission;

            const { parentProductIds, productIds } =
              this.getIdsFromProducts(products);

            await this.handleGetHomeFeatures(parentProductIds, productIds, null)
              .then((featuresResponse) => {
                features = featuresResponse;
              })
              .catch((error) => {
                console.log(error + ' ' + features);
              });
          })
          .catch((error) => {
            console.log(error + ' ' + products + ' ' + suppliers);
          });
      })
      .catch((error) => {
        console.log(error + ' ' + packages);
      });

    return formatterHome(
      packages,
      products,
      suppliers,
      features,
      commonTechnical,
      getTypeFromQueryParams(_searchCriteria),
      productIncentiveDetails,
      productCommissions
    );
  }

  async getProductsTripleplay(_searchCriteria: object): Promise<IProduct[]> {
    let calculateParameters: CalculateParametersTripleplay = {
      postalCode: getSafeData(_searchCriteria['postcode'], ''),
      houseNumber: getSafeData(_searchCriteria['housenumber'], ''),
      houseNumberAddition: getSafeData(
        _searchCriteria['housenumberaddition'],
        ''
      ),
      comparisonId: getSafeData(_searchCriteria['comparisonId'], ''),
      retention: getSafeData(_searchCriteria['currentProvider'], ''),
      filters: getSafeData(_searchCriteria['filterGraphQL'], []),
    };

    let packages: Package[] = [];
    let products: TripleplayProduct[] = [];
    let comparisonId = '';
    await this.calculateTriplePlay(
      calculateParameters.postalCode,
      calculateParameters.houseNumber,
      calculateParameters.houseNumberAddition,
      calculateParameters.filters,
      '', // calculateParameters.retention,
      calculateParameters.comparisonId
    )
      .then(async (data) => {
        const suppliers = await this.getProductsSupplier();
        const scoresEntities = await this.getScoreEntities();
        const resultedProducts = data.products.map((product) => {
          const currentSupplier = suppliers.find(
            (s) => s.name === product.supplier.name
          );
          if (currentSupplier) {
            product = {
              ...product,
              supplier: {
                ...product.supplier,
                ...currentSupplier,
              },
            };
          }
          const currentScoreEntity = scoresEntities.find(
            (s) => s.supplierKrn === product.supplier.krn
          );
          const productType = getProductType(product.servicesWrapper);
          if (currentScoreEntity) {
            product.scoreEntity = {
              ...currentScoreEntity,
              scores: currentScoreEntity.scores.filter(
                (score) => score.scoreCategory === productType
              ),
            };
          }
          return product;
        });
        comparisonId = data.comparisonId;
        products = resultedProducts;
        return data;
      })
      .catch((error) => {
        console.log(error + ' ' + packages);
      });
    return formatterTripleplay(
      products,
      getTypeFromQueryParams(_searchCriteria),
      comparisonId
    );
  }

  async getProductsOsTripleplay(_searchCriteria: object): Promise<IProduct[]> {
    let calculateParameters: CalculateParametersTripleplay = {
      postalCode: getSafeData(_searchCriteria['postcode'], ''),
      houseNumber: getSafeData(_searchCriteria['housenumber'], ''),
      houseNumberAddition: getSafeData(
        _searchCriteria['housenumberaddition'],
        ''
      ),
      filters: getSafeData(_searchCriteria['additionalChannels'], []),
      comparisonId: getSafeData(_searchCriteria['comparisonId'], ''),
      productTypes: getSafeData(_searchCriteria['productTypes'], [
        'television',
      ]),
    };

    let packages: Package[] = [];
    let products: OsTripleplayProduct[] = [];
    let productIncentiveDetails: ProductIncentiveDetail[] = [];
    let commonTechnical: CommonTechnical[] = [];

    await this.calculateOsTriplePlay(
      calculateParameters.postalCode,
      calculateParameters.houseNumber,
      calculateParameters.houseNumberAddition,
      calculateParameters.productTypes
    )
      .then(async (data) => {
        let productKrns: string[] = [];
        data.products.map((product) => {
          let productKrn = product?.productCode;
          if (productKrn && !productKrns.includes(productKrn)) {
            productKrns.push(productKrn);
          }
        });

        if (Array.isArray(data?.commonTechnical)) {
          commonTechnical = data.commonTechnical;
        }

        let response;
        try {
          response = await this.getProductsWithSupliers(
            productKrns,
            'TRIPLE_PLAY'
          );
        } catch (error) {
          console.log({ error });
        }
        const resultedProducts = data.products
          .filter((product) => {
            const currentProduct = response.products.find(
              (p) => p.krn === product.productCode
            );
            if (currentProduct) {
              product.name = currentProduct?.name;
              product.sellingPoints = currentProduct?.sellingPoints;
              product.productIncentives =
                currentProduct?.productIncentives || [];
            }
            const currentSupplier = response.suppliers.find((s) =>
              product.companyNameDescription
                .toLowerCase()
                .includes(s.name.toLowerCase())
            );
            if (currentSupplier) {
              product.supplier = {
                ...product.supplier,
                ...currentSupplier,
              };
              return true;
            }
            return false;
          })
          .map((product) => ({
            ...product,
          }));
        products = resultedProducts;
        productIncentiveDetails = response.productIncentiveDetails ?? [];

        return data;
      })
      .catch((error) => {
        console.log(error + ' ' + packages);
      });
    return formatterOsTripleplay(
      products,
      commonTechnical,
      JSON.parse(calculateParameters.filters),
      productIncentiveDetails
    );
  }

  async getProducts(
    _searchCriteria: object,
    selectedFilters: object
  ): Promise<IProduct[]> {
    const type = getTypeFromQueryParams(_searchCriteria);
    if (type === TYPE_HOME) {
      return this.getProductsHome(_searchCriteria, selectedFilters);
    } else if (type === TYPE_TRIPLEPLAY) {
      return this.getProductsTripleplay(_searchCriteria);
    } else if (type === TYPE_OS_TRIPLEPLAY) {
      return this.getProductsOsTripleplay(_searchCriteria);
    } else {
      return this.getProductsHome(_searchCriteria, selectedFilters);
    }
  }

  getIdsFromProducts(products: HomeProduct[]): {
    parentProductIds: string[];
    productIds: string[];
  } {
    let parentProductIds: string[] = [];
    let productIds: string[] = [];

    for (const product of products) {
      if (product.dataProviderKrn.includes('moneyview')) {
        if (product.productType === 'insurance/package') {
          parentProductIds.push(product.externalId);
        } else {
          productIds.push(product.externalId);
        }
      }
    }

    return {
      parentProductIds,
      productIds,
    };
  }

  async getComparison(comparisonId: string): Promise<Comparison> {
    await this.getToken();

    const graphURL = this.baseUrl;
    const query = getComparison;
    const variables = JSON.stringify({
      comparisonId,
    });
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.credentials.token,
      },
      body: buildGraphQL(query, variables),
    };
    const res = await fetch(graphURL, requestOptions);

    if (!res.ok || !res.json) throw new Error('500');
    const body = await res.json();
    if (!body.data || body.data.length < 1) throw new Error('400');
    const buildings =
      body.data.calculationInput.masterAgreementPackages[0].objectBuildings[0];
    const postcode = buildings.addresses[0].postalCode;
    const housenumber = buildings.addresses[0].houseNumber;
    const houseLetter = buildings.addresses[0].houseLetter;
    const coverageHomes =
      body.data.calculationInput.masterAgreementPackages[0].coverageHomes;
    const coverageBuildings =
      body.data.calculationInput.masterAgreementPackages[0].coverageBuildings;
    const coveragetype = getCoverageCode(coverageHomes, coverageBuildings);
    const familyComposition = getFamilyComposition(
      coverageHomes,
      coverageBuildings
    );
    const sumInsured = getSumInsured(coverageHomes, coverageBuildings);
    const coverageadditions = calculateCoverageAdditions(
      coverageHomes,
      coverageBuildings,
      body.data.calculationInput.masterAgreementPackages[0].coverageLiabilities,
      body.data.calculationInput.masterAgreementPackages[0].objectGlasses
    );
    const filters = calculateFilters(
      body.data.calculationInput.masterAgreementPackages[0],
      coveragetype
    );
    let address;
    try {
      address = await this.validateAddress(postcode, housenumber, houseLetter);
    } catch (error) {
      console.warn('Address is invalid');
    }
    return {
      criteria: {
        housenumber,
        ...address,
        housenumberaddition: houseLetter,
        hometype: {
          getUserInput: buildings.propertyOfInsured ? '2' : '1',
          getHouseInput: buildings.homeType,
        },
        familyComposition,
        coveragetype,
        coverageadditions,
        constructionyear: buildings.constructionYear,
        rooftype: buildings.constructionOfRoof,
        mezzaninefloor: buildings.constructionOfFloors === 'VT' ? '1' : '2',
        consturctionwalls: buildings.constructionOfWalls === 'VT' ? '1' : '2',
        solarPanels: buildings.solarPanels ? '1' : '2',
        renovated: buildings.recentBathroomKitchenLivingRoom ? '1' : '0',
        security: buildings.burglaryPreventionMeasuresPresent,
        surfacesize: buildings.houseSurfaceAreaSquareMeters,
        contentsize: buildings.volumeInCubicMeters,
        propertyofinsured: buildings.propertyOfInsured,
        rebuildCost: buildings.rebuildCost,
        wozAppraisalValue: buildings.wozAppraisalValue,
        homeSumInsured: sumInsured,
        birthdate: moment(
          body.data.calculationInput.masterAgreementPackages[0]
            .underwritingValuePreventionMeters[0].dateBirthMainBreadwinner
        ).format('DD-MM-YYYY'),
        income:
          body.data.calculationInput.masterAgreementPackages[0]
            .underwritingValuePreventionMeters[0]
            .monthlyNetIncomeMainBreadwinner,
        filters: JSON.stringify(filters),
      },
    };
  }

  async validateAddress(
    postalCode: string,
    houseNumber: string,
    houseNumberAddition?: string | undefined
  ): Promise<AddressApiSuccessResponse> {
    await this.getToken();
    const query = getHouseDataQuery;
    const variables = {
      postalCode: postalCode,
      houseNumber: parseInt(houseNumber),
      houseNumberAddition: houseNumberAddition || '',
    };

    const graphURL = `${process.env.KIZI_PUBLIC_DOMAIN}/housedata/graphql`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.credentials.token,
      },
      body: buildGraphQL(query, JSON.stringify(variables)),
    };

    return new Promise<AddressApiSuccessResponse>((resolve, reject) => {
      fetch(graphURL, requestOptions).then(async (response) => {
        const { errors, data } = await response.json();
        errors && reject(errors);

        const { simpleAddressData } = data;
        if (simpleAddressData && simpleAddressData.postalCode) {
          resolve({
            city: simpleAddressData.place ?? '',
            street: simpleAddressData.street ?? '',
            houseNumber: (simpleAddressData.houseNumber ?? '').toString(),
            postcode: simpleAddressData.postalCode ?? '',
            fullAddress: `${simpleAddressData.street} ${
              simpleAddressData.houseNumber
            }${
              simpleAddressData.houseNumberAddition
                ? '-' + simpleAddressData.houseNumberAddition
                : ''
            } ${simpleAddressData.postalCode}, ${simpleAddressData.place}`,
            addition: simpleAddressData.houseNumberAddition ?? '',
          });
        } else {
          reject(houseNumberError);
        }
      });
    });
  }

  async calculateHomeContentPackage(
    calculateParameters: CalculateParameters
  ): Promise<MasterAgreementStructure> {
    await this.getToken();

    this.calculateSessionID = getCalculateSessionId();

    const homePackage = {
      masterAgreementPackages: [
        {
          objectBuildings: [
            {
              addresses: [
                {
                  houseLetter: calculateParameters.houseLetter,
                  houseNumber: parseInt(calculateParameters.houseNumber),
                  postalCode: calculateParameters.postalCode,
                },
              ],
              burglaryPreventionMeasuresPresent:
                calculateParameters.burglaryPrevention,
              consideredMonumentByOwner:
                // @ts-ignore:next-line
                calculateParameters.consideredMonumentByOwner === 'true',
              constructionOfWalls: calculateParameters.constructionOfWalls,
              constructionOfFloors: calculateParameters.constructionOfFloors,
              constructionOfRoof: calculateParameters.constructionOfRoof,
              constructionYear: calculateParameters.constructionYear,
              homeType: calculateParameters.homeType,
              houseSurfaceAreaSquareMeters:
                calculateParameters.houseSurfaceAreaSquareMeters,
              numberRooms: calculateParameters.numberRooms,
              propertyOfInsured: calculateParameters.propertyOfInsured,
              propertyUseType: calculateParameters.propertyUseType,
              rebuildCost: calculateParameters.rebuildCost,
              recentBathroomKitchenLivingRoom:
                calculateParameters.recentBathroomKitchen == 1,
              solarPanels: calculateParameters.solarPanels == 1,
              volumeInCubicMeters: calculateParameters.volumeInCubicMeters,
              wozAppraisalValue: calculateParameters.wozAppraisalValue,
            },
          ],
          effectiveDate: calculateParameters.effectiveDate,
          paymentTermInMonths: calculateParameters.paymentTermInMonths,
          coverageBuildings: '$coverageBuildings',
          coverageHomes: '$coverageHomes',
          coverageLiabilities: '$coverageLiabilities',
          objectGlasses: [
            {
              translucentMaterialType: calculateParameters.glass,
            },
          ],
          partyPolicyHolders: [
            {
              birthDate: calculateParameters.birthDate,
            },
          ],
          underwritingValuePreventionMeters: [
            {
              dateBirthMainBreadwinner: calculateParameters.birthDate,
              monthlyNetIncomeMainBreadwinner:
                calculateParameters.netMonthlyIncome,
            },
          ],
        },
      ],
      commonTechnical: [
        {
          senderId: this.credentials.clientId,
          sessionId: this.calculateSessionID,
        },
      ],
    };

    const query = calculateHomeQuery;
    let variables = JSON.stringify({
      request: homePackage,
    });

    variables = calculateParameters.includeCoverageBuildings
      ? variables.replace(
          '"$coverageBuildings"',
          coverageBuildingsQuery
            .replace(
              '$coverageCodeBuildings',
              `"${calculateParameters.coverageCodeBuildings}"`
            )
            .replace(
              '$deductibleAmount',
              `${calculateParameters.deductibleAmount}`
            )
            .replace(
              '$insuredFamilyComposition',
              `"${calculateParameters.insuredFamilyComposition}"`
            )
        )
      : variables.replace('"$coverageBuildings"', '[]');
    variables = replaceCoverageHomeQuery(calculateParameters, variables);
    variables =
      calculateParameters.includeCoverageLiabilities &&
      Number(calculateParameters.liabilitySumInsured) > 0
        ? variables
            .replace('"$coverageLiabilities"', coverageLiabilitiesQuery)
            .replace(
              '$liabilitySumInsured',
              (calculateParameters.liabilitySumInsured ?? 1250000.0).toString(
                10
              )
            )
            .replace(
              '$deductibleAmount',
              `${calculateParameters.deductibleAmount ?? 0}`
            )
            .replace(
              '$insuredFamilyComposition',
              calculateParameters.insuredFamilyComposition ?? 'VH'
            )
        : variables.replace('"$coverageLiabilities"', '[]');

    const graphURL = `${process.env.KIZI_PUBLIC_DOMAIN}/home-content-package/graphql`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.credentials.token,
      },
      body: buildGraphQL(query, variables),
    };

    return new Promise<MasterAgreementStructure>((resolve, reject) => {
      fetch(graphURL, requestOptions)
        .then(handleResponse)
        .then((data) => {
          if (
            data.calculateHomeContentPackageV2
              ?.homeContentPackageSchema_MasterAgreementStructure
          ) {
            resolve(
              data.calculateHomeContentPackageV2
                ?.homeContentPackageSchema_MasterAgreementStructure as MasterAgreementStructure
            );
          } else {
            reject(houseNumberError);
          }
        })
        .catch((status) => {
          if ([401].indexOf(status) != -1) {
            //token expired probably
            this.credentials.token = '';
            return this.calculateHomeContentPackage(calculateParameters)
              .then((data) => {
                resolve(data);
              })
              .catch((error) => {
                reject(error);
              });
          }

          reject(status);
          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        }); //this is needed so we do not get warning for unhandled rejection
    });
  }

  async getAddressData(
    postalCode: string,
    houseNumber: string,
    houseNumberAddition?: string
  ): Promise<AddressData> {
    await this.getToken();

    const query = addressDataQuery;
    const variables = JSON.stringify({
      postalCode: postalCode,
      houseNumber: parseInt(houseNumber),
      houseNumberAddition: houseNumberAddition,
    });
    const graphURL = `${process.env.KIZI_PUBLIC_DOMAIN}/housedata/graphql`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.credentials.token,
      },
      body: buildGraphQL(query, variables),
    };

    return new Promise<AddressData>((resolve, reject) => {
      fetch(graphURL, requestOptions)
        .then(handleResponse)
        .then((data) => {
          if (data.addressData) {
            //needs to be resolved here so that server would not return error if you reuse the data.
            //server is returning VZ but if you send VZ you get error.
            data.addressData.homeType =
              data.addressData.homeType == 'VZ'
                ? 'VX'
                : data.addressData.homeType;
            resolve(data.addressData);
          } else {
            reject('addressData is missing');
          }
        })
        .catch((status) => {
          if ([401].indexOf(status) != -1) {
            //token expired probably
            this.credentials.token = '';
            return this.getAddressData(postalCode, houseNumber)
              .then((data) => {
                resolve(data);
              })
              .catch((error) => {
                reject(error);
              });
          }

          reject(status);
          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        }); //this is needed so we do not get warning for unhandled rejection
    });
  }

  async calculateTriplePlay(
    postalCode: string,
    houseNumber: string,
    houseNumberAddition: string,
    filters: string,
    retention: string,
    comparisonId = ''
  ): Promise<Tripleplay> {
    await this.getToken();

    const query = calculateTriplePlayQuery;
    const variables = {
      postalCode: postalCode,
      houseNumber: parseInt(houseNumber),
      houseNumberAddition: houseNumberAddition,
      comparisonId: comparisonId,
      filters: JSON.parse(filters),
      retention: retention,
    };

    return new Promise<Tripleplay>((resolve, reject) => {
      this.getClient()
        .query({
          query,
          variables,
          fetchPolicy: 'network-only',
          context: {
            uri: `${process.env.KIZI_PUBLIC_DOMAIN}/triple-play/graphql`,
          },
        })
        .then(({ data, errors }) => {
          errors && reject(errors);
          if (data.calculateTriplePlay) {
            const { comparisonId: id, products } = data.calculateTriplePlay;
            resolve({
              comparisonId: id,
              products: products,
            });
          } else reject('no data found for calculateTriplePlay');
        })
        .catch((status) => {
          if ([401].indexOf(status) != -1) {
            //token expired probably
            this.credentials.token = '';
            return this.calculateTriplePlay(
              postalCode,
              houseNumber,
              filters,
              retention,
              comparisonId
            )
              .then((data) => {
                resolve(data);
              })
              .catch((error) => {
                reject(error);
              });
          }

          reject(status);
          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        }); //this is needed so we do not get warning for unhandled rejection
    });
  }

  async calculateOsTriplePlay(
    postalCode: string,
    houseNumber: string,
    houseNumberAddition: string,
    productTypes?: string
    // filters: string,
  ): Promise<OsTripleplay> {
    await this.getToken();

    const parsedProductTypes = JSON.parse(productTypes || '["internet"]');

    const variables = {
      request: {
        policyPolicyDetails: [
          {
            partyPolicyHolders: [
              {
                addresses: [
                  {
                    postalCode: postalCode,
                    houseNumber: parseInt(houseNumber),
                    houseNumberAddition: houseNumberAddition,
                  },
                ],
              },
            ],
            // paymentTermInMonths: 1, TODO
            customProperties: {
              customAttributes: [
                {
                  description: 'internet',
                  value: 'true',
                },
                {
                  description: 'tv',
                  value: parsedProductTypes.includes('television')
                    ? 'true'
                    : 'false',
                },
                {
                  description: 'phone',
                  value: parsedProductTypes.includes('call') ? 'true' : 'false',
                },
              ],
            },
          },
        ],
        commonTechnical: [
          {
            senderId: this.credentials.clientId,
          },
        ],
      },
    };

    return new Promise<OsTripleplay>((resolve, reject) => {
      this.getClient()
        .mutate({
          mutation: calculateOsTriplePlayQuery,
          variables,
          fetchPolicy: 'network-only',
          context: {
            uri: `${process.env.KIZI_PUBLIC_DOMAIN}/triple-play-v2/graphql`,
          },
        })
        .then(({ data, errors }) => {
          errors && reject(errors);

          if (data?.calculateTriplePlay?.calculationResult) {
            const { policyPolicyDetails, commonTechnical } =
              data.calculateTriplePlay.calculationResult;
            resolve({ products: policyPolicyDetails, commonTechnical });
          } else reject('no data found for calculate, OsTriplePlay');
        })
        .catch((status) => {
          if ([401].indexOf(status) != -1) {
            //token expired probably
            this.credentials.token = '';
            return this.calculateOsTriplePlay(
              postalCode,
              houseNumber,
              houseNumberAddition
              // filters,
            )
              .then((data) => {
                resolve(data);
              })
              .catch((error) => {
                reject(error);
              });
          }

          reject(status);
          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        }); //this is needed so we do not get warning for unhandled rejection
    });
  }

  async getCustomerBenefits(): Promise<CustormerBenefitItem[]> {
    await this.getToken();

    const query = getCustomerBenefits;
    const variables = '';

    const graphURL = `${process.env.KIZI_PUBLIC_DOMAIN}/triple-play/graphql`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.credentials.token,
      },
      body: buildGraphQL(query, variables),
    };

    const handleRes = (data) => {
      const {
        customerBenefit: { provider },
      } = data;

      return provider
        .reduce((acc, curProvider) => {
          const { name, product } = curProvider;
          const products = product?.mobile?.product ?? [];
          const defaultItem = products.find((i) => !i.text);
          products.forEach((p) => {
            const { text, id } = p;
            const item: { label: string; value: any; dependsOn?: any } = {
              label: text ? `${name} ${text}` : name,
              value: id,
            };
            if (text) item.dependsOn = defaultItem.id;
            acc.push(item);
          });
          return acc;
        }, [])
        .filter(({ dependsOn, label, value }, _, arr) =>
          dependsOn
            ? !arr.some((o) => o.value === value && o.label !== label)
            : true
        );
    };

    return new Promise<CustormerBenefitItem[]>((resolve, reject) => {
      fetch(graphURL, requestOptions)
        .then(handleResponse)
        .then((data) => {
          resolve(handleRes(data));
        })
        .catch((status) => {
          if ([401].indexOf(status) != -1) {
            //token expired probably
            this.credentials.token = '';
            return this.getCustomerBenefits()
              .then((data) => {
                resolve(data);
              })
              .catch((error) => {
                reject(error);
              });
          }

          reject(status);
          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getToken(): Promise<void> {
    if (
      this.credentials.token === '' ||
      moment().isAfter(this.credentials.expiresIn)
    ) {
      await this.acquireToken()
        .then(handleResponse)
        .then((data) => {
          this.credentials.token = data.access_token;
          this.credentials.expiresIn = moment().add(
            data.expires_in - 1,
            'seconds'
          );
        })
        .catch((status) => {
          return Promise.reject(status);
        });
    }
  }

  async getProductsWithSupliers(
    productKrns: string[],
    productCategory: string
  ): Promise<ProductsWithSuppliers> {
    await this.getToken();

    const query = productsWithSuppliersQuery;
    const variables = JSON.stringify({
      productKrns: productKrns,
      productCategory: productCategory,
    });
    const graphURL = `${process.env.KIZI_PUBLIC_DOMAIN}/product/graphql`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.credentials.token,
      },
      body: buildGraphQL(query, variables),
    };

    return new Promise<ProductsWithSuppliers>((resolve, reject) => {
      fetch(graphURL, requestOptions)
        .then(handleResponse)
        .then((data) => {
          if (data.productsWithSuppliers) {
            resolve(data.productsWithSuppliers);
          } else {
            reject('productsWithSuppliers is missing');
          }
        })
        .catch((status) => {
          if ([401].indexOf(status) != -1) {
            //token expired probably
            this.credentials.token = '';
            return this.getProductsWithSupliers(productKrns, productCategory)
              .then((data) => {
                resolve(data);
              })
              .catch((error) => {
                reject(error);
              });
          }

          reject(status);
          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        }); //this is needed so we do not get warning for unhandled rejection
    });
  }

  async getProductsSupplier(): Promise<Array<ProductSupplier>> {
    await this.getToken();

    return new Promise<Array<ProductSupplier>>((resolve, reject) => {
      this.getClient()
        .query({
          query: getProductsSupplierQuery,
          context: {
            uri: `${process.env.KIZI_PUBLIC_DOMAIN}/product/graphql`,
          },
        })
        .then(({ data, errors }) => {
          if (data.productSuppliersByProviderKrn) {
            const resolveData = data.productSuppliersByProviderKrn;
            return resolve(resolveData);
          } else {
            reject('productSuppliersByProviderKrn are missing');
          }

          if (errors) {
            reject(errors);
          }
        })
        .catch((status) => {
          if ([401].indexOf(status) != -1) {
            this.credentials.token = '';
            return this.getProductsSupplier()
              .then((data) => {
                return resolve(data);
              })
              .catch((error) => {
                reject(error);
              });
          }

          reject(status);
          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getOrderDefinition(
    product: IProduct
  ): Promise<LegacyOrderResponse['data']> {
    await this.getToken();
    const subscriptionId = parseInt(product.id);

    return new Promise<LegacyOrderResponse['data']>((resolve, reject) => {
      this.getClient()
        .query({
          query: getForms,
          variables: { subscriptionId },
          context: {
            uri: `${process.env.KIZI_PUBLIC_DOMAIN}/triple-play/graphql`,
          },
        })
        .then(({ data, errors }) => {
          if (data.formsTriplePlay) {
            return resolve([data.formsTriplePlay]);
          }
          if (errors) {
            reject(errors);
          }
        })
        .catch((status) => {
          if ([401].indexOf(status) != -1) {
            this.credentials.token = '';
            return this.getOrderDefinition(product)
              .then((data) => {
                return resolve(data);
              })
              .catch((error) => {
                reject(error);
              });
          }
          reject(status);
          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getScoreEntities(): Promise<ScoreEntity[]> {
    await this.getToken();

    const query = getScoreEntitiesQuery;
    const variables = getScoreEntitiesVariables();

    return new Promise<ScoreEntity[]>((resolve, reject) => {
      this.getClient()
        .query({
          query,
          variables,
          context: {
            uri: `${process.env.KIZI_PUBLIC_DOMAIN}/product/graphql`,
          },
        })
        .then(({ data }) => {
          if (data?.scoreEntitiesForQuarterAndYear) {
            const resolveData = data.scoreEntitiesForQuarterAndYear;
            return resolve(resolveData);
          } else {
            reject('scoreEntities is missing');
          }
        })
        .catch((status) => {
          if ([401].indexOf(status) != -1) {
            this.credentials.token = '';
            return this.getScoreEntities()
              .then((data) => resolve(data))
              .catch((error) => reject(error));
          }

          reject(status);
          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async handleGetHomeFeatures(
    parentProductIds: string[],
    productIds: string[],
    after: string | null,
    alreadyLoadedFeatures: FeaturesEdge[] = []
  ): Promise<FeaturesEdge[]> {
    let features: FeaturesEdge[] = alreadyLoadedFeatures;

    return this.getHomeFeatures(parentProductIds, productIds, after).then(
      async ({ edges, pageInfo }) => {
        if (pageInfo.hasNextPage) {
          return this.handleGetHomeFeatures(
            parentProductIds,
            productIds,
            pageInfo.endCursor,
            [...features, ...edges]
          );
        } else {
          return [...features, ...edges];
        }
      }
    );
  }

  async getHomeFeatures(
    parentProductIds: string[],
    productIds: string[],
    after: string | null
  ): Promise<FeaturesConnection> {
    await this.getToken();

    const query = getHomeFeaturesQuery;
    const variables = JSON.stringify({
      after,
      where: {
        and: [
          {
            or: [
              {
                parentProductId: {
                  in: parentProductIds.map((id) => parseInt(id)),
                },
              },
              {
                productId: {
                  in: productIds.map((id) => parseInt(id)),
                },
              },
            ],
          },
          {
            externalFeatureId: {
              in: [
                30100, 22054, 30103, 46914, 25893, 22060, 22061, 21213, 21212,
                53448, 30098, 30096, 30097, 22058, 53342, 53343,
              ],
            },
          },
        ],
      },
    });
    const graphURL = `${process.env.KIZI_PUBLIC_DOMAIN}/features/graphql`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.credentials.token,
      },
      body: buildGraphQL(query, variables),
    };

    return new Promise<FeaturesConnection>((resolve, reject) => {
      fetch(graphURL, requestOptions)
        .then(handleResponse)
        .then((data) => {
          if (data.features) {
            resolve(data.features);
          } else {
            reject('home features are missing');
          }
        })
        .catch(({ status }) => {
          if ([401].indexOf(status) != -1) {
            this.credentials.token = '';

            return this.getHomeFeatures(parentProductIds, productIds, null)
              .then((data) => {
                resolve(data);
              })
              .catch((error) => {
                reject(error);
              });
          }

          reject(status);
          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async handleGetHomeFeaturesDetails(
    productId: number | number[],
    after: string | null,
    alreadyLoadedFeatures: FeaturesEdge[] = []
  ): Promise<FeaturesEdge[]> {
    let features: FeaturesEdge[] = alreadyLoadedFeatures;

    return this.getHomeFeaturesDetails(productId, after).then(
      async ({ edges, pageInfo }) => {
        if (pageInfo.hasNextPage) {
          return this.handleGetHomeFeaturesDetails(
            productId,
            pageInfo.endCursor,
            [...features, ...edges]
          );
        } else {
          return [...features, ...edges];
        }
      }
    );
  }

  async getHomeFeaturesDetails(
    productId: number | number[],
    after: string | null
  ): Promise<FeaturesConnection> {
    await this.getToken();

    const query = getHomeFeaturesQuery;
    const _productId = Array.isArray(productId)
      ? {
          productId: {
            in: productId,
          },
        }
      : {
          productId: {
            eq: productId,
          },
        };
    const variables = JSON.stringify({
      after,
      where: {
        ..._productId,
      },
    });
    const graphURL = `${process.env.KIZI_PUBLIC_DOMAIN}/features/graphql`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.credentials.token,
      },
      body: buildGraphQL(query, variables),
    };

    return new Promise<FeaturesConnection>((resolve, reject) => {
      fetch(graphURL, requestOptions)
        .then(handleResponse)
        .then((data) => {
          if (data.features) {
            resolve(data.features);
          } else {
            reject('home features are missing');
          }
        })
        .catch(({ status }) => {
          if ([401].indexOf(status) != -1) {
            this.credentials.token = '';

            return this.getHomeFeaturesDetails(productId, null)
              .then((data) => {
                resolve(data);
              })
              .catch((error) => {
                reject(error);
              });
          }

          reject(status);
          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async orderAffiliateContentPackage(
    transactionReference: string,
    productCode: string,
    affiliateIncentive,
    comparatorName?: string
  ): Promise<OrderResult> {
    console.log({ comparatorName });
    if (comparatorName === 'os-triple-play') {
      return this.orderOsTriplePlayAffiliate(transactionReference, productCode);
    }
    await this.getToken();

    const query = orderHomeQuery;
    const variables = JSON.stringify({
      comparisonId: transactionReference,
      chosenProductKrn: productCode,
      frontEndName: 'overstappen',
      package: {},
      clientId: this.credentials.clientId,
      googleAnalyticsId: getCookie('_ga'),
      googleAnalyticsSessionId: getCookie(getAnalyticsSessionIdCookieName()),
      fbp: getCookie('_fbp'),
      fbc: getCookie('_fbc'),
      vwoId: getCookie('_vwo_uuid'),
      gclid: localStorage.getItem('gclid'),
    });

    console.log({ variables });
    const graphURL = `${process.env.KIZI_PUBLIC_DOMAIN}/home-content-package/graphql`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.credentials.token,
      },
      body: buildGraphQL(query, variables),
    };

    return new Promise<OrderResult>((resolve, reject) => {
      fetch(graphURL, requestOptions)
        .then(handleResponse)
        .then((data) => {
          if (data.orderHomeContentPackage) {
            resolve(data.orderHomeContentPackage);
          } else {
            reject('home orderHomeContentPackage are missing');
          }
        })
        .catch((status) => {
          if ([401].indexOf(status) != -1) {
            //token expired probably
            this.credentials.token = '';
            return this.orderAffiliateContentPackage(
              transactionReference,
              productCode,
              affiliateIncentive,
              comparatorName
            )
              .then((data) => {
                resolve(data);
              })
              .catch((error) => {
                reject(error);
              });
          }

          reject(status);
          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        }); //this is needed so we do not get warning for unhandled rejection
    });
  }

  async orderOsTriplePlayAffiliate(
    transactionReference: string,
    productCode: string
  ): Promise<OrderResult> {
    const variables = {
      comparisonId: transactionReference,
      chosenProductKrn: productCode,
      input: {
        commonTechnical: {
          senderId: this.credentials.clientId ?? '',
        },
      },
      clientId: this.credentials.clientId,
      frontEndName: 'overstappen',
      googleAnalyticsId: getCookie('_ga'),
      googleAnalyticsSessionId: getCookie(getAnalyticsSessionIdCookieName()),
      fbp: getCookie('_fbp'),
      fbc: getCookie('_fbc'),
      vwoId: getCookie('_vwo_uuid'),
      gclid: localStorage.getItem('gclid'),
    };

    return new Promise<OrderResult>((resolve, reject) => {
      this.getClient()
        .mutate({
          mutation: orderTriplePlay,
          variables,
          fetchPolicy: 'network-only',
          context: {
            uri: `${process.env.KIZI_PUBLIC_DOMAIN}/triple-play-v2/graphql`,
          },
        })
        .then(({ data }) => {
          resolve({
            orderId: data?.orderTriplePlay?.orderResult?.orderId,
            errors: [],
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async sendEmailToCustomer(
    comparisonId: string | number,
    email: string,
    productType: string,
    inactivity: boolean
  ): Promise<string> {
    await this.getToken();

    const graphURL = `${process.env.KIZI_PUBLIC_DOMAIN}/events/graphql`;
    const query = inactivity
      ? sendInactivityEmailToCustomerQuery
      : sendEmailToCustomerQuery;
    const variables = JSON.stringify({
      comparisonId,
      email,
      productType,
    });
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.credentials.token,
      },
      body: buildGraphQL(query, variables),
    };
    return new Promise((resolve) => {
      fetch(graphURL, requestOptions).then(() => {
        resolve('Email sent');
      });
    });
  }

  async getCalculationInput(
    transactionReference: string
  ): Promise<MasterAgreementStructure> {
    await this.getToken();

    const query = calculationInputQuery;
    const variables = JSON.stringify({
      comparisonId: transactionReference,
    });

    const graphURL = this.baseUrl;
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.credentials.token,
      },
      body: buildGraphQL(query, variables),
    };

    return new Promise<MasterAgreementStructure>((resolve, reject) => {
      fetch(graphURL, requestOptions)
        .then(handleResponse)
        .then((data) => {
          if (data.calculationInput) {
            resolve(data.calculationInput as MasterAgreementStructure);
          } else {
            reject('calculationInput is missing');
          }
        })
        .catch((status) => {
          if ([401].indexOf(status) != -1) {
            //token expired probably
            this.credentials.token = '';
            return this.getCalculationInput(transactionReference)
              .then((data) => {
                resolve(data);
              })
              .catch((error) => {
                reject(error);
              });
          }

          reject(status);
          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        }); //this is needed so we do not get warning for unhandled rejection
    });
  }

  async orderStatus(orderId: string): Promise<OrderStatusResult> {
    await this.getToken();

    const query = orderStatusQuery;
    const variables = JSON.stringify({
      orderId,
    });

    const graphURL = this.baseUrl;
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.credentials.token,
      },
      body: buildGraphQL(query, variables),
    };

    return new Promise<OrderStatusResult>((resolve, reject) => {
      fetch(graphURL, requestOptions)
        .then(handleResponse)
        .then((data) => {
          if (data.orderStatus) {
            resolve(data.orderStatus);
          } else {
            reject('home orderStatus is missing');
          }
        })
        .catch((status) => {
          if ([401].indexOf(status) != -1) {
            //token expired probably
            this.credentials.token = '';
            return this.orderStatus(orderId)
              .then((data) => {
                resolve(data);
              })
              .catch((error) => {
                reject(error);
              });
          }

          reject(status);
          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async checkMembershipStatus(email: string) {
    await this.getToken();

    const query = swiftMemberExistsQuery;
    const graphURL = `${process.env.KIZI_PUBLIC_DOMAIN}/shared/services/graphql?sdl=true`;

    const variables = JSON.stringify({
      swiftMemberCheck: {
        email,
      },
      correlationId: `membership-check-${Date.now()}`,
    });
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.credentials.token,
      },
      body: buildGraphQL(query, variables),
    };
    return new Promise((resolve, reject) => {
      fetch(graphURL, requestOptions)
        .then(handleResponse)
        .then((res) => {
          resolve(res.swiftMemberExists.exists);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}
